import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  service_code: '',
  service_name: '',
  posting_type: '',
  source_module: 'FRONT_OFFICE',
  revenue_code: '',
  revenue_description: '',
  sac_code: '',
  is_active: true
};

function PmsServiceMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/pms/service-master?all=1', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load service master');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load service master');
    }
  };

  useEffect(() => {
    setError('');
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.service_code || '').trim()) return setError('Service Code is required');
    if (!String(form.service_name || '').trim()) return setError('Service Name is required');
    if (!String(form.posting_type || '').trim()) return setError('Posting Type is required');
    setError('');
    try {
      const url = editingId ? `/api/v1/pms/service-master/${editingId}` : '/api/v1/pms/service-master';
      const method = editingId ? 'PUT' : 'POST';
      const payload = {
        service_code: String(form.service_code || '').trim().toUpperCase(),
        service_name: String(form.service_name || '').trim(),
        posting_type: String(form.posting_type || '').trim().toUpperCase(),
        source_module: String(form.source_module || 'FRONT_OFFICE').trim().toUpperCase(),
        revenue_code: String(form.revenue_code || '').trim().toUpperCase(),
        revenue_description: String(form.revenue_description || '').trim(),
        sac_code: String(form.sac_code || '').trim(),
        is_active: form.is_active !== false
      };
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      setForm(emptyForm);
      setEditingId('');
      loadRows();
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this service?')) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/service-master/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Deactivate failed');
      loadRows();
    } catch (err) {
      setError(err?.message || 'Deactivate failed');
    }
  };

  const editRow = (row) => {
    setEditingId(String(row.service_id));
    setForm({
      service_code: row.service_code || '',
      service_name: row.service_name || '',
      posting_type: row.posting_type || '',
      source_module: row.source_module || 'FRONT_OFFICE',
      revenue_code: row.revenue_code || '',
      revenue_description: row.revenue_description || '',
      sac_code: row.sac_code || '',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Service Master</h3>
      {error ? <div className="error-banner">{error}</div> : null}

      <div ref={formRef} className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Service Code *</span>
          <input value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} placeholder="EX: LAUNDRY" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Service Name *</span>
          <input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} placeholder="EX: Laundry" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Posting Type *</span>
          <input value={form.posting_type} onChange={(e) => setForm({ ...form, posting_type: e.target.value })} placeholder="EX: LAUNDRY" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Source Module</span>
          <select value={form.source_module} onChange={(e) => setForm({ ...form, source_module: e.target.value })}>
            <option value="FRONT_OFFICE">Front Office</option>
            <option value="HOUSEKEEPING">Housekeeping</option>
            <option value="KOT_POS">KOT/POS</option>
            <option value="BAR">Bar</option>
            <option value="BANQUET">Banquet</option>
            <option value="MANUAL">Manual</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Revenue Code</span>
          <input value={form.revenue_code} onChange={(e) => setForm({ ...form, revenue_code: e.target.value })} placeholder="EX: RC_LAUNDRY" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Revenue Description</span>
          <input value={form.revenue_description} onChange={(e) => setForm({ ...form, revenue_description: e.target.value })} placeholder="EX: Laundry Service" />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>SAC Code</span>
          <input value={form.sac_code} onChange={(e) => setForm({ ...form, sac_code: e.target.value })} placeholder="EX: 996311" />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Active
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button onClick={save}>{editingId ? 'Update Service' : 'Add Service'}</button>
          {editingId ? <button onClick={() => { setForm(emptyForm); setEditingId(''); }}>Cancel</button> : null}
        </div>
      </div>

      <div style={{ marginTop: '0.8rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Posting Type</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Source</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Revenue Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Revenue Description</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>SAC Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.service_id} style={{ borderBottom: '1px solid var(--theme-border)', background: row.is_active ? '#fff' : 'var(--theme-bg-light)' }}>
                <td style={{ padding: '0.45rem' }}>{row.service_code}</td>
                <td style={{ padding: '0.45rem' }}>{row.service_name}</td>
                <td style={{ padding: '0.45rem' }}>{row.posting_type}</td>
                <td style={{ padding: '0.45rem' }}>{row.source_module}</td>
                <td style={{ padding: '0.45rem' }}>{row.revenue_code || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{row.revenue_description || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{row.sac_code || '-'}</td>
                <td style={{ padding: '0.45rem', color: row.is_active ? '#166534' : '#b91c1c' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.35rem' }}>
                  <button onClick={() => editRow(row)}>Edit</button>
                  {row.is_active ? <button onClick={() => remove(row.service_id)}>Deactivate</button> : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={9} style={{ padding: '0.7rem', color: 'var(--theme-text-secondary)' }}>No services configured.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsServiceMaster;
